/**
 * Store identity helpers. `storeInitials` is the monogram shown when a store
 * has no logo; the storefront (`cartadigital-app`
 * `shared/utils/store-initials.utils.ts`) applies the same rule and vectors.
 */

const LETTER = /\p{L}/u;
const MIN_WORD_LETTERS = 3;

function letters(word: string): string[] {
  return Array.from(word).filter((char) => LETTER.test(char));
}

/** The first letter of a word, else its first character. */
function initialOf(word: string): string {
  return letters(word)[0] ?? Array.from(word)[0] ?? '';
}

/**
 * Words of ≥3 letters, the first letter of the first two; one such word → its
 * first letter; none → the first letter of the trimmed name; uppercase with
 * accents kept; empty → «?». «La Esquina de Tito» → «ET», «El Sol» → «S».
 */
export function storeInitials(name: string | null | undefined): string {
  // NFC keeps a decomposed «N» + tilde as one «Ñ» letter (same as the storefront).
  const trimmed = (name ?? '').normalize('NFC').trim();
  if (!trimmed) return '?';
  const words = trimmed
    .split(/\s+/)
    .filter((word) => letters(word).length >= MIN_WORD_LETTERS);
  const initials = words.length
    ? words.slice(0, 2).map(initialOf).join('')
    : initialOf(trimmed);
  return initials.toUpperCase() || '?';
}

/** The link as the owner reads it: «bistri.fi/{slug}». */
export function storeLinkLabel(slug: string): string {
  return 'bistri.fi/' + slug;
}

/** The storefront URL; callers pass `Globals.shortWebsite` as `baseUrl`. */
export function storeLinkUrl(slug: string, baseUrl: string): string {
  return baseUrl + '/' + slug;
}
